import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import type { SiteRagReport } from '../../../types/siteRag';
import { reportSummaryEligibility } from '../../../lib/reportSummary/eligibility';
import { reportSummaryPath } from '../../../lib/siteNavigation';
import { useStore } from '../../../store';
import { Button } from '../../ui/button';
import { downloadReportSummary, prebuildReportSummary } from './reportSummaryArtifactActions';

export function ReportSummaryActions({ report, siteId, onOpenSummary }: {
  report: SiteRagReport; siteId: string; onOpenSummary?: (reportId: string) => void;
}) {
  const navigate = useNavigate();
  const companyId = useStore(s => s.companyId);
  const [pending, setPending] = useState(false);
  const eligible = reportSummaryEligibility(report).ok;
  const actionIdentity = `${companyId ?? ''}::${siteId}::${report.id}::${report.reportRevision?.revisionId ?? ''}`;
  const actionIdentityRef = React.useRef(actionIdentity);
  const reqSeqRef = React.useRef(0);
  const lifecycleEpochRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  if (actionIdentityRef.current !== actionIdentity) {
    actionIdentityRef.current = actionIdentity;
    lifecycleEpochRef.current += 1;
    reqSeqRef.current += 1;
  }
  const isActionRequestCurrent = (
    requestIdentity: string,
    requestSeq: number,
    requestEpoch: number,
  ): boolean =>
    isMountedRef.current
    && lifecycleEpochRef.current === requestEpoch
    && actionIdentityRef.current === requestIdentity
    && reqSeqRef.current === requestSeq;
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      lifecycleEpochRef.current += 1;
      reqSeqRef.current += 1;
    };
  }, []);
  useEffect(() => {
    setPending(false);
    if (eligible) prebuildReportSummary(report, undefined, companyId ?? null);
  }, [eligible, actionIdentity]);
  if (!eligible) return null;
  return <>
    <Button size="sm" variant="outline" className="min-h-11" data-testid={`findings-summary-${report.reportType}`} onClick={() => onOpenSummary ? onOpenSummary(report.id) : navigate(reportSummaryPath(siteId, report.id))}><FileText aria-hidden />Summary</Button>
    <Button size="sm" variant="ghost" className="min-h-11" data-testid={`findings-summary-pdf-${report.reportType}`} aria-label="Download three-page report summary PDF" disabled={pending} onClick={() => {
      const requestIdentity = actionIdentity;
      const requestEpoch = lifecycleEpochRef.current;
      const requestSeq = ++reqSeqRef.current;
      setPending(true);
      void downloadReportSummary(report, {
        companyId: companyId ?? null,
        shouldSave: () => isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch),
      }).catch(error => {
        if (isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
          toast.error((error as Error).message);
        }
      }).finally(() => {
        if (isActionRequestCurrent(requestIdentity, requestSeq, requestEpoch)) {
          setPending(false);
        }
      });
    }}><Download aria-hidden />{pending ? 'Building…' : 'PDF'}</Button>
  </>;
}
